import { Formik, Form, Field, ErrorMessage } from "formik"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import {
  adForgeFiltersSchema,
  type AdForgeFilters,
  sanitizeValidatedFilters,
} from "./validation-schema"

interface Campaign {
  id: string
  name: string
  status?: string
  spend?: number
}

interface FiltersModalProps {
  isOpen: boolean
  onClose: () => void
  campaigns: Campaign[]
  filters: AdForgeFilters
  onFiltersChange: (filters: AdForgeFilters) => void
}

export default function FiltersModal({
  isOpen,
  onClose,
  campaigns,
  filters,
  onFiltersChange,
}: FiltersModalProps) {
  const initialValues: AdForgeFilters = {
    campaignFilter: filters.campaignFilter || "",
    statusFilter: filters.statusFilter || "ACTIVE",
    minSpend: filters.minSpend || "",
    maxSpend: filters.maxSpend || "",
    searchQuery: filters.searchQuery || "",
  }

  const handleSubmit = (values: AdForgeFilters) => {
    try {
      // Sanitize and format the validated values
      const sanitizedFilters = sanitizeValidatedFilters(values)
      onFiltersChange(sanitizedFilters)
      onClose()
    } catch (error) {
      console.error("Error submitting filters:", error)
      // Still close the modal to prevent getting stuck
      onClose()
    }
  }

  const handleReset = (resetForm: () => void) => {
    const resetFilters: AdForgeFilters = {
      campaignFilter: "",
      statusFilter: "ACTIVE",
      minSpend: "",
      maxSpend: "",
      searchQuery: "",
    }
    resetForm()
    onFiltersChange(resetFilters)
  }

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={500}
      classes={{
        container:
          "bg-system-blue-800 border-2 border-system-blue-600 rounded-xl p-6 max-w-md w-full",
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={adForgeFiltersSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ resetForm, isValid }) => (
          <Form>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Filters</h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Campaign Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                  Campaign
                </label>
                <Field
                  as="select"
                  name="campaignFilter"
                  className="w-full px-4 py-2 bg-system-blue-700 border border-system-blue-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                >
                  <option value="">All Campaigns</option>
                  {campaigns.map(campaign => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}{" "}
                      {campaign.spend
                        ? `($${campaign.spend.toLocaleString()})`
                        : ""}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="campaignFilter"
                  component="div"
                  className="text-red-400 text-sm mt-1"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                  Status
                </label>
                <Field
                  as="select"
                  name="statusFilter"
                  className="w-full px-4 py-2 bg-system-blue-700 border border-system-blue-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                >
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="DELETED">Deleted</option>
                  <option value="ARCHIVED">Archived</option>
                </Field>
                <ErrorMessage
                  name="statusFilter"
                  component="div"
                  className="text-red-400 text-sm mt-1"
                />
              </div>

              {/* Spend Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                  Spending ($)
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Field name="minSpend">
                      {({
                        field,
                        meta,
                      }: {
                        field: {
                          value: string
                          name: string
                          onChange: (e: {
                            target: { name: string; value: string }
                          }) => void
                        }
                        meta: { error?: string; touched?: boolean }
                      }) => (
                        <input
                          {...field}
                          type="number"
                          placeholder="Min"
                          className={`w-full px-4 py-2 bg-system-blue-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${
                            meta.error && meta.touched
                              ? "border-red-500"
                              : "border-system-blue-600"
                          }`}
                          min="0"
                          step="0.01"
                          value={field.value || ""}
                          onChange={e => {
                            const value = e.target.value
                            field.onChange({
                              target: {
                                name: field.name,
                                value: value === "" ? "" : value,
                              },
                            })
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="minSpend"
                      component="div"
                      className="text-red-400 text-xs mt-1"
                    />
                  </div>
                  <div className="flex items-center text-gray-400">
                    <span>to</span>
                  </div>
                  <div className="flex-1">
                    <Field name="maxSpend">
                      {({
                        field,
                        meta,
                      }: {
                        field: {
                          value: string
                          name: string
                          onChange: (e: {
                            target: { name: string; value: string }
                          }) => void
                        }
                        meta: { error?: string; touched?: boolean }
                      }) => (
                        <input
                          {...field}
                          type="number"
                          placeholder="Max"
                          className={`w-full px-4 py-2 bg-system-blue-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${
                            meta.error && meta.touched
                              ? "border-red-500"
                              : "border-system-blue-600"
                          }`}
                          min="0"
                          step="0.01"
                          value={field.value || ""}
                          onChange={e => {
                            const value = e.target.value
                            field.onChange({
                              target: {
                                name: field.name,
                                value: value === "" ? "" : value,
                              },
                            })
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="maxSpend"
                      component="div"
                      className="text-red-400 text-xs mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Filter by daily budget amount (converted from cents)
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-8 justify-start">
              <button
                type="submit"
                disabled={!isValid}
                className="px-6 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={() => handleReset(resetForm)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-system-blue-600 text-white rounded-lg hover:bg-system-blue-500 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
